import React, { useRef, useEffect, useMemo } from "react";
import { Form, Input, Select, TimePicker, Radio, Row, Col, Switch } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import DaySelector from "../../../../Common/DaySelector";
import { useStaffTableGet } from "../../../../../hooks/api/staff";
import { useLocationsDictionary } from "../../../../../hooks/api/locations";
import { LazySelect } from "../../../../Common/SelectLazyLoader";
import { parseDefaultTime } from "../../../../../utils";

const AUDIENCE_OPTIONS = [
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
];

interface VaadUser {
  id: string;
  firstName: string;
  lastName: string;
}

interface VaadCreateFormProps {
  initialUser?: VaadUser | null;
  mode?: "create" | "edit";
}

export default function VaadCreateForm({
  initialUser,
  mode,
}: VaadCreateFormProps): React.ReactElement {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const statusValue = Form.useWatch("status", form);
  const countryValue = Form.useWatch("country", form);
  const stateValue = Form.useWatch("state", form);
  const staffTableGet = useStaffTableGet();
  const locations = useLocationsDictionary();

  const countryOptions = (locations.data || []).map((c) => ({
    label: c.country,
    value: c.country,
  }));

  const selectedCountry = (locations.data || []).find(
    (c) => c.country === countryValue,
  );

  const stateOptions = (selectedCountry?.states || []).map((s) => ({
    label: s.state,
    value: s.state,
  }));

  const selectedState = (selectedCountry?.states || []).find(
    (s) => s.state === stateValue,
  );

  const cityOptions = (selectedState?.cities || []).map((city) => ({
    label: city,
    value: city,
  }));
  const hasCityOptions = cityOptions.length > 0;

  // Timezone options are derived from the locations dictionary (country + state
  // timezones), so the list always matches the backend. Deduped and sorted.
  const timezoneOptions = useMemo(() => {
    const zones = new Set<string>();
    (locations.data || []).forEach((c) => {
      if (c.timezone) zones.add(c.timezone);
      (c.states || []).forEach((s) => {
        if (s.timezone) zones.add(s.timezone);
      });
    });
    return Array.from(zones)
      .sort()
      .map((tz) => ({ value: tz, label: tz }));
  }, [locations.data]);

  const leaderMapRef = useRef<Map<string, { name: string; id: string }>>(
    new Map(),
  );

  useEffect(() => {
    if (initialUser) {
      leaderMapRef.current.set(initialUser.id, {
        name: `${initialUser.firstName} ${initialUser.lastName}`,
        id: initialUser.id,
      });
    }
  }, [initialUser]);

  return (
    <>
      {/* Vaad Name */}
      <Form.Item
        label="Vaad Name"
        name="name"
        rules={[{ required: true, message: "Vaad name is required" }]}
      >
        <Input placeholder="e.g., Simcha Chart 3" />
      </Form.Item>

      {/* Audience */}
      <Form.Item label="Audience" name="type">
        <Select placeholder="Select one" options={AUDIENCE_OPTIONS} />
      </Form.Item>

      {/* Leader Name */}
      <Form.Item label="Leader Name" name="user">
        <LazySelect
          initialOptions={
            initialUser
              ? [
                {
                  label: `${initialUser.firstName} ${initialUser.lastName}`,
                  value: initialUser.id,
                },
              ]
              : undefined
          }
          fetchOptions={async ({ search, page, pageSize }) => {
            const res = await staffTableGet.fetch({
              page,
              pageSize,
              search,
            });

            const data = res?.data || [];

            data.forEach((item: any) => {
              leaderMapRef.current.set(item.id, {
                name: item.fullName || `${item.firstName} ${item.lastName}`,
                id: item.id,
              });
            });

            return data;
          }}
          mapOption={(item) => ({
            label: item.fullName || `${item.firstName} ${item.lastName}`,
            value: item.id,
          })}
          onChange={(value: string) => {
            const selected = leaderMapRef.current.get(value);
            form.setFieldsValue({
              id: selected?.id || "",
            });
          }}
          pageSize={20}
        />
      </Form.Item>

      {/* Vaad ID + Default Time */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Vaad ID"
            name="vaadId"
            normalize={(value) => {
              if (!value) return value;
              return value.toUpperCase().replace(/[a-z]/g, "").slice(0, 5);
            }}
            rules={[
              { required: true, message: "Vaad ID is required" },
              {
                pattern: /^[^a-z]{1,5}$/,
                message:
                  "Vaad ID must be up to 5 uppercase characters, digits and special symbols allowed",
              },
            ]}
          >
            <Input placeholder="Type here" maxLength={5} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Default Time"
            name="defaultTime"
            getValueProps={(value) => {
              const parsed = parseDefaultTime(value);
              return {
                value: parsed
                  ? dayjs().hour(parsed.hour).minute(parsed.minute).second(0)
                  : null,
              };
            }}
            getValueFromEvent={(time) => {
              return time ? time.format("HH:mm") : null;
            }}
          >
            <TimePicker
              placeholder="Select time"
              format="h:mm A"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Default Day */}
      <Form.Item label="Default Day" name="defaultDay">
        <DaySelector />
      </Form.Item>

      {/* Country + State + City */}
      <Form.Item
        label="Country"
        name="country"
        rules={[{ required: true, message: "Country is required" }]}
      >
        <Select
          placeholder="Select country"
          options={countryOptions}
          loading={locations.loading}
          showSearch
          optionFilterProp="label"
          onChange={(value) => {
            const countryTz = (locations.data || []).find(
              (c) => c.country === value,
            )?.timezone;
            form.setFieldsValue({
              state: undefined,
              city: undefined,
              // Suggest the country's timezone; a state pick refines it below.
              ...(countryTz ? { timezone: countryTz } : {}),
            });
          }}
        />
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="State"
            name="state"
            rules={[{ required: true, message: "State is required" }]}
          >
            <Select
              placeholder="Select state"
              options={stateOptions}
              disabled={!countryValue}
              showSearch
              optionFilterProp="label"
              onChange={(value) => {
                const stateTz = (selectedCountry?.states || []).find(
                  (s) => s.state === value,
                )?.timezone;
                form.setFieldsValue({
                  city: undefined,
                  // State timezone is more precise than the country default.
                  ...(stateTz ? { timezone: stateTz } : {}),
                });
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="City"
            name="city"
            rules={[{ required: hasCityOptions, message: "City is required" }]}
          >
            <Select
              placeholder={
                stateValue && !hasCityOptions
                  ? "No cities available"
                  : "Select city"
              }
              options={cityOptions}
              disabled={!stateValue || !hasCityOptions}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Timezone */}
      <Form.Item label="Timezone" name="timezone">
        <Select
          placeholder="Select timezone"
          options={timezoneOptions}
          loading={locations.loading}
          showSearch
          optionFilterProp="label"
          allowClear
        />
      </Form.Item>

      {/* Address */}
      <Form.Item label="Address" name="address">
        <Input placeholder="e.g. 2118 Thornridge Cir. Syracuse, Connecticut 35624" />
      </Form.Item>
      {mode === "edit" && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span style={{ fontSize: 14 }}>
              Vaad is {statusValue === "active" ? "active" : "inactive"}
            </span>
            <Form.Item
              style={{ margin: 0 }}
              name="status"
              valuePropName="checked"
              getValueProps={(value) => ({
                checked: value === "active" || value === true,
              })}
              getValueFromEvent={(checked) => (checked ? "active" : "inactive")}
            >
              <Switch />
            </Form.Item>
          </div>
          {/* <Form.Item
            name="status"
            valuePropName="checked"
            getValueProps={(value) => ({
              checked: value === "active" || value === true,
            })}
            getValueFromEvent={(checked) =>
              checked ? "active" : "not_started"
            }
          >
            <Switch />
          </Form.Item> */}
          <div
            style={{
              marginTop: 8,
              marginBottom: 12,
              fontSize: 12,
            }}
          >
            Deactivation of the Vaad does not cancel their Stripe subscription
            automatically
          </div>
        </>
      )}
    </>
  );
}
